import { useState, useCallback } from "react";
import {
  Button,
  ButtonVariant,
  CheckboxCard,
  BaseInput,
  Show
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { DropZone } from "shared/components/DropZone";
import { CombinedIncomeLayout } from "../components/Layout";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { useMutation } from "@tanstack/react-query";
import { IncomeType, VerifyMethodType } from "shared/types";
import { useFormTyped } from "shared/hooks";
import { useCombinedIncome } from "../context";
import { CombinedIncomeSubStep } from "../../../constants";
import { getErrorMessage } from "utils/error";
import { uploadFilesToS3 } from "../utils";
import { normalizeFilename, getFileUploadErrorMessage } from "shared/utils";

const VALID_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "application/pdf"
];

type DocumentFormData = Record<string, string>;

export const HousingAssistanceStep = () => {
  const {
    application,
    applicant,
    verification,
    refetchVerification,
    refetchApplication,
    onNextStep,
    createIncomeMutation,
    updateStepMutation
  } = useCombinedIncome();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isHousingAssistanceProgram, setIsHousingAssistanceProgram] =
    useState<boolean>();

  const { onSubmit, register, values } = useFormTyped<DocumentFormData>({
    amount: ""
  });

  const finishVerificationMutation = useMutation({
    mutationFn: async () => {
      await instance.post(
        `/portal/v1/applications/${application.id}/verifications/${verification?.id}/finish?applicant_id=${applicant.id}`
      );
    },

    onSuccess: () => {
      updateStepMutation.mutate(
        {
          lastStep: CombinedIncomeSubStep.Completed
        },
        {
          onSuccess: () => {
            refetchVerification();
            onNextStep();
          },
          onError: (error) => {
            toast.error(
              getErrorMessage(error) || "Failed to complete housing assistance"
            );
          }
        }
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to complete verification");
    }
  });

  const uploadDocumentsMutation = useMutation({
    mutationFn: async ({
      incomeId,
      formValues
    }: {
      incomeId: number;
      formValues: DocumentFormData;
    }) => {
      const uploadedUrls = await uploadFilesToS3(uploadedFiles);

      const documents = uploadedFiles.map((file, idx) => ({
        source: formValues[`source_${idx}`] || undefined,
        document_type: VerifyMethodType.HousingAssistance,
        url: uploadedUrls[idx],
        metadata: {
          size: file.size,
          original_filename: normalizeFilename(file.name),
          content_type: file.type
        }
      }));

      const response = await instance.post(
        `/portal/v1/applications/${application.id}/verifications/${verification?.id}/income/${incomeId}/create_income_source_with_documents?applicant_id=${applicant.id}`,
        {
          income_source_type: VerifyMethodType.HousingAssistance,
          documents
        }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Documents uploaded successfully");
      updateStepMutation.mutate(
        {
          lastStep: CombinedIncomeSubStep.Completed
        },
        {
          onSuccess: () => {
            finishVerificationMutation.mutate();
          },
          onError: (error) => {
            toast.error(
              getErrorMessage(error) || "Failed to complete housing assistance"
            );
          }
        }
      );
    },
    onError: (error) => {
      toast.error(
        getFileUploadErrorMessage(error) ||
          getErrorMessage(error) ||
          "Failed to upload documents"
      );
    }
  });

  const handleSubmit = useCallback(
    async (formValues: DocumentFormData) => {
      if (isHousingAssistanceProgram)
        createIncomeMutation.mutate(
          {
            type: IncomeType.HousingAssistance,
            amount: {
              cents: Number(formValues.amount) || 0,
              currency: "USD"
            }
          },
          {
            onSuccess: (data) => {
              const incomeId = data.id;
              if (incomeId)
                uploadDocumentsMutation.mutate({ incomeId, formValues });
              else toast.error("Failed to retrieve income ID");
            },
            onError: (error) => {
              toast.error(
                getErrorMessage(error) || "Failed to create housing assistance"
              );
            }
          }
        );
      else finishVerificationMutation.mutate();
    },
    [
      isHousingAssistanceProgram,
      uploadedFiles,
      createIncomeMutation,
      uploadDocumentsMutation,
      application.id,
      verification?.id,
      applicant.id,
      refetchVerification,
      refetchApplication,
      onNextStep
    ]
  );

  const isButtonDisabled = () => {
    if (isHousingAssistanceProgram === undefined) return true;

    if (isHousingAssistanceProgram) {
      const hasAmount = values.amount && Number(values.amount) > 0;
      const hasDocument = uploadedFiles.length > 0;
      return !hasAmount || !hasDocument;
    }

    return false;
  };

  return (
    <form
      onSubmit={onSubmit({
        onSuccess: (formValues) => {
          handleSubmit(formValues);
        },
        onError: () => {
          toast.error("Please select an income source for all documents");
        }
      })}
    >
      <CombinedIncomeLayout
        title="Are you applying as a part of housing assistance program (e.g. Section 8)?"
        footer={
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-[132px]"
            type="submit"
            loading={
              uploadDocumentsMutation.isPending ||
              createIncomeMutation.isPending
            }
            disabled={isButtonDisabled()}
          >
            Continue
          </Button>
        }
      >
        <div className="mt-4 flex w-full gap-2">
          <CheckboxCard
            checked={isHousingAssistanceProgram === true}
            id="yes"
            label="Yes"
            name="yes"
            wrapperClassName="w-full"
            className="h-[72px] flex items-center justify-center"
            onChange={() => setIsHousingAssistanceProgram(true)}
          />
          <CheckboxCard
            checked={isHousingAssistanceProgram === false}
            id="no"
            label="No"
            name="no"
            wrapperClassName="w-full"
            className="h-[72px] flex items-center justify-center"
            onChange={() => setIsHousingAssistanceProgram(false)}
          />
        </div>
        <Show if={isHousingAssistanceProgram}>
          <div className="mt-6 border-t border-alpha-100 pt-6" />
          <BaseInput
            parser={moneyCentsParser({ asString: true })}
            id="housing-assistance-amount"
            label="Monthly assistance amount"
            prefix="$"
            placeholder="0.00"
            required
            {...register("amount")}
          />
          <div className="mt-4">
            <DropZone
              id="housing-assistance-upload"
              title="Upload assistance documentation"
              subText="e.g., voucher, lease, etc."
              value={uploadedFiles}
              setValue={setUploadedFiles}
              accept={VALID_FILE_TYPES.join(",")}
              multiple
            />
          </div>
        </Show>
      </CombinedIncomeLayout>
    </form>
  );
};
